import { t } from 'i18next';
import { scoreToFive } from '@/utils/score';
import { Survey } from '@/services/surveys-service/surveys-service.types';
import { RATING_LADDER, RAW_SCORE_MAX } from './satisfaction.constants';

export type RatingRow = { stars: number; count: number; percent: number };

/**
 * Five bars, 5 down to 1. Nothing outside 1-10 is a rating: zero is counted for the
 * caption, while out-of-scale and negative scores are dropped without a trace. The upper
 * bound is checked before scoreToFive, which saturates at 5 rather than rejecting.
 */
export function toRatingRows(scores: (number | null)[]): RatingRow[] {
  const counts = new Map<number, number>(RATING_LADDER.map((stars) => [stars, 0]));

  for (const score of scores) {
    // Null arm is for the type checker; `null > 10` is already false.
    if (score !== null && score > RAW_SCORE_MAX) continue;

    const outOfFive = scoreToFive(score, 'scale10');
    if (outOfFive === null) continue;

    const stars = Math.round(outOfFive);
    if (!counts.has(stars)) continue;

    counts.set(stars, (counts.get(stars) ?? 0) + 1);
  }

  // Against the widest bar, not the total, as the mockup scales it.
  const widest = Math.max(...counts.values(), 1);

  return RATING_LADDER.map((stars) => {
    const count = counts.get(stars) ?? 0;

    return { stars, count, percent: (count / widest) * 100 };
  });
}

export type Quote = {
  id: number;
  text: string;
  topic: string | null;
  /** On the 0-5 scale. Null is defensive: no quoted row has arrived without a rating. */
  stars: number | null;
};

export const QUOTE_LIMIT = 3;

/** Words, not digits: a reply of `9.5` or `1` is a rating, and quoting it reads as one. */
const hasWords = (text: string) => /\p{L}/u.test(text);

/** A rating this row opens with: its own score, punctuation, then words. */
const OPENING_SCORE = /^(\d+)(?:[.,]\d+)?\s*[-–—:.,]\s*(?=\D|$)/;

/**
 * The reply without the rating it opens with: the stars carry it, the quotation marks must
 * not. Punctuation is required, so "2 dni czekalem" survives at any score, and the token
 * must be the whole leading number, or "9,5 bardzo dobrze" would be quoted as "5 bardzo".
 */
function withoutOpeningScore(text: string, score: number): string {
  const opening = OPENING_SCORE.exec(text);

  if (!opening || Number(opening[1]) !== score) return text;

  return text.slice(opening[0].length).trim();
}

/**
 * The newest replies that carry words, in the order they arrived. The rating is shown as
 * it came and never filters. Phone stays off, unlike the surveys table a clerk searched.
 */
export function toQuotes(surveys?: Survey[], limit = QUOTE_LIMIT): Quote[] {
  return (surveys ?? [])
    .map((survey) => ({
      survey,
      text: withoutOpeningScore((survey.body ?? '').trim(), survey.score),
    }))
    .filter((entry) => hasWords(entry.text))
    .slice(0, limit)
    .map(({ survey, text }) => ({
      id: survey.id,
      text,
      topic: survey.topic ?? null,
      stars: scoreToFive(survey.score, 'scale10'),
    }));
}

export function countZeros(scores: (number | null)[]): number {
  return scores.filter((score) => score === 0).length;
}

/**
 * The bars count a capped, recency-ordered sample, so they can cover fewer ratings than
 * the headline. Naming both counts is the only thing on screen that shows the gap.
 */
export function sampleCaption({
  rated,
  responses,
  zeros,
  sampled,
  atCeiling,
}: {
  rated: number;
  responses: number | null;
  zeros: number;
  sampled: number;
  atCeiling: boolean;
}): string {
  // Truncation comes from the request, not from comparing the counts: the aggregate can
  // fail while the bars still render, and claiming the whole range then is the one thing
  // this caption exists to prevent.
  const scope = atCeiling
    ? t('satisfaction:sample.scopeRecent', { count: sampled })
    : t('satisfaction:sample.scopeAll', { count: sampled });
  const bars = t('satisfaction:sample.bars', { count: rated });
  const of = responses === null ? '' : ` ${t('satisfaction:sample.ofTotal', { responses })}`;
  const unscored = zeros ? [t('satisfaction:sample.unscored', { count: zeros })] : [];

  // Clauses in one fixed order, joined here rather than in the dictionary: two of the four
  // are optional, and a key per combination would say the same thing sixteen ways.
  return `${[`${bars}${of}`, scope, ...unscored].join(', ')}.`;
}
